import 'piral/polyfills'
import * as React from 'react'
import { createRoot } from 'react-dom/client'
import { createInstance, createStandardApi, Piral } from 'piral'
import { init as initSpatialNavigation } from '@noriginmedia/norigin-spatial-navigation'
import { layout, errors } from './layout'
import { createI18nApi } from './i18nApi'
import { createConfigApi } from './config/configApi'
import { FEED_URL } from './config/feed'
import { createTelemetryApi } from './telemetry/telemetryApi'
import { createAnalyticsApi } from './analytics/analyticsApi'
import { createListsApi } from './lists/listsApi'
import { initTelemetry, setTelemetryTenant } from './telemetry/faro'
import { loadDomainIdentity } from './auth/whoami'
import { initConsentLoaders } from './consent/consentLoaders'
// Design tokens (the CSS custom properties on :root) live in the styleguide —
// the single source of truth, shared by the shell, Storybook and every pilet.
// Import them once here, before the component styles that consume them.
import '@medialib/styleguide/tokens.css'
// The app shell owns the styleguide's stylesheet — import it exactly once here
// so every pilet that uses a styleguide component inherits the styles.
import '@medialib/styleguide/styleguide.css'
// Web fonts are self-hosted (bundled by webpack, served same-origin) so nothing
// loads from a third-party CDN pre-consent — this keeps the visitor IP off
// Google Fonts / Cloudflare for GDPR. Lato in the weights the app actually uses
// (100/300/400/700/900) plus Font Awesome solid icons.
import '@fontsource/lato/100.css'
import '@fontsource/lato/300.css'
import '@fontsource/lato/400.css'
import '@fontsource/lato/700.css'
import '@fontsource/lato/900.css'
import '@fortawesome/fontawesome-free/css/all.min.css'
// The shell creates and configures the single shared i18next instance (like the
// styleguide CSS and the spatial-navigation singleton, it is set up exactly once
// here). Pilets add their own translation catalogs to this instance in setup().
import './i18n'

// Frontend observability. First statement of the shell on purpose, and it has to stay
// first: it installs the global error/rejection handlers before any other startup code
// can throw, AND patches fetch/XHR before any of the requests below are issued —
// `loadDomainIdentity` here, the domain configuration inside `createConfigApi`, the
// pilet feed. Those all fire in this same synchronous block, so anything installed
// later never sees them.
//
// This is the FAULT stage — errors and request traces, no session id, no storage, no
// behavioural signals; the audience-measurement half is unlocked by consent below.
initTelemetry()

// Scope every signal to the tenant this deployment serves. medialib is multi-tenant —
// one codebase, many libraries, each on its own hostname — so a fault affecting one of
// them is invisible inside the aggregate of all of them without this.
//
// Started here, at module scope, rather than from AuthProvider's mount effect: whoami
// is the only endpoint carrying the tenant, and every millisecond between Faro coming
// up and this landing is a window where an error is reported unattributed. The request
// is shared, so AuthProvider reuses this very one rather than issuing a second.
// Resolves on the anonymous 403 too — `domain` is on that body as well.
loadDomainIdentity().then(setTelemetryTenant)

// TV / remote-control navigation. The shell initialises the spatial-navigation
// singleton exactly once — the library keeps its focus tree in module scope, so
// (like use-persisted-state) it MUST be a shared dependency in the importmap or
// each pilet would get its own disconnected focus tree. Styleguide components
// (ScrollRow) register their focusable cells against this single instance.
initSpatialNavigation({
  // Arrow keys + Enter are wired by default; enable visualDebug to see the
  // focus grid overlay while iterating (`just dev`).
  debug: false,
  visualDebug: false,
})

// Bind the consent-gated loaders (analytics) to the consent decision before the app
// renders. Nothing beacons out until the user grants the category — the
// ConsentProvider records and broadcasts the choice, this reacts to it and loads.
// This is also what promotes Faro from errors-only to full RUM.
initConsentLoaders()

// Deploy-time, not build-time: a production release points this at its own immutable
// /r/<version>/pilets.json, so shell and pilets are promoted (and rolled back) as one
// unit. See src/config/feed.ts.
const feedUrl = FEED_URL

const instance = createInstance({
  // createI18nApi adds `registerTranslations` to every pilet's API, so pilets ship
  // and lazily register their own translation catalogs (see src/i18nApi.ts).
  // createConfigApi loads the domain configuration once and adds
  // `getDomainConfiguration` (carrying the session CSRF token) to every pilet's API.
  // createTelemetryApi adds `trackEvent`/`trackMeasurement`/`trackError`, so pilets
  // instrument through the shell's single Faro instance instead of bundling their own.
  // createAnalyticsApi adds `trackGoal` — the audience-measurement counterpart, going
  // to Plausible behind the same consent gate as the script itself.
  // createListsApi adds the account's saved lists (favorites, watch-later): the shell
  // holds one membership manifest, so every toggle in every pilet agrees about a title.
  plugins: [
    ...createStandardApi(),
    createI18nApi(),
    createConfigApi(),
    createTelemetryApi(),
    createAnalyticsApi(),
    createListsApi(),
  ],
  requestPilets () {
    return fetch(feedUrl)
      .then(res => res.json())
      .then(res => res.items)
  },
  state: {
    components: layout,
    errorComponents: errors,
  },
})

const container = document.querySelector('#app')
if (!container) {
  throw new Error('Shell mount point #app not found in index.html')
}

const root = createRoot(container)
root.render(<Piral instance={instance} />)
